import { computed } from '@angular/core';
import { readonly, validateStandardSchema } from '@angular/forms/signals';

import type { ExtendedPositionLegacy, LegacyTextDefault, TextEntity, WidgetPositionLegacy } from '@sites/entity-core';
import { textLegacyDefaultV0Schema, textLegacyZeroV0Schema } from '@sites/entity-core';

import type { BaseLegacyEntityForm, BaseLegacyEntityFormOptions } from '../../core/create-base-legacy-entity-form';
import { createBaseLegacyEntityForm } from '../../core/create-base-legacy-entity-form';
import { composeForm } from '../../core/compose-form';
import { animationPlugin } from '../../plugins/widget/animation.plugin';
import { visibilityPlugin } from '../../plugins/widget/visibility.plugin';
import { legacyWidgetEnvelopePlugin } from '../../plugins/widget/legacy-widget-envelope.plugin';

export type TextFormModel = TextEntity;
export type TextDataModel = TextEntity['data'];

/**
 * Internal editing model the FieldTree is typed against. The two variants
 * share every field except `position`'s TYPE, so — like the subscription-form
 * widget — we widen `position` to the union and keep ONE assembler (no
 * video-style per-variant duplication). A single concrete object type also
 * sidesteps `FieldTree<union>` resolution pitfalls.
 */
type TextFormEntity = Omit<LegacyTextDefault, 'type' | 'data'> & {
    type?: 'default' | 'zero';
    data: Omit<LegacyTextDefault['data'], 'position'> & { position: WidgetPositionLegacy | ExtendedPositionLegacy };
};

/**
 * Signal-form view of the text widget. Single factory for both placement
 * variants: the variants share every field — only `position`'s TYPE differs
 * (simple grid vs extended grid) — so the validation schema is dispatched on
 * `options.initialData?.type` while the accessor surface stays one assembler.
 *
 * **Field accessors are FieldTree references** — exposed via getters so the
 * FieldTree paths materialise lazily (after `syncFromEntity` populates the
 * model). Convenience `setX(v)` methods are sugar over `field().value.set(v)`.
 */
export function createTextForm(options?: BaseLegacyEntityFormOptions<TextEntity>) {
    // Variant is fixed at construction time — the zero envelope requires the
    // `type: 'zero'` tag and the extended-position grid; everything else is
    // shared between the schemas.
    const isZero = options?.initialData?.type === 'zero';

    // Each variant schema's output is NARROWER than the widened editing model
    // (`type` pinned per branch, `position` concrete per branch), so the
    // ternary's union doesn't satisfy `StandardSchemaV1<TextFormEntity>`
    // structurally — the runtime contract is what matters here.
    const schema = (isZero ? textLegacyZeroV0Schema : textLegacyDefaultV0Schema) as any;

    const base = createBaseLegacyEntityForm<TextFormEntity>((schemaPath) => {
        validateStandardSchema(schemaPath, schema);
        readonly(schemaPath.id);
        readonly(schemaPath.name);
        readonly(schemaPath.columnId);
    }, options as BaseLegacyEntityFormOptions<TextFormEntity>);

    // `_raw` stays typed as `FieldTree<TextFormEntity>` so each accessor below
    // resolves to the correct nested `FieldTree<…>` (`position` is the
    // simple/extended union — narrow at the consumer per variant).
    const _raw = base._raw;

    // Checked widening: the base methods (`syncFromEntity`, `setValue`,
    // `value`) are widened to operate on `TextEntity` so callers that only
    // know the public union get a useful shape. The editing model
    // (`TextFormEntity`) widens `position` and loosens `type`, so the
    // assignment goes through `unknown` (the FormGroup-era cast is preserved).
    const widenedBase = base as unknown as BaseLegacyEntityForm<TextEntity>;

    return composeForm(
        widenedBase,
        // Envelope (`name` / `columnId` / `type` discriminator / `displayId`).
        legacyWidgetEnvelopePlugin(() => _raw),
        // Per-device visibility flags.
        visibilityPlugin(() => _raw.data),
        // Animation settings group.
        animationPlugin(() => _raw.data),

        // --- Text-only fields (content + layout + sync flag + union position) ---
        {
            // --- Content ---
            get content() {
                return _raw.data.content;
            },
            setContent: (value: string) => {
                _raw.data.content().value.set(value);
            },

            // --- Layout ---
            get horizontalAlign() {
                return _raw.data.horizontalAlign;
            },
            setHorizontalAlign: (value: TextDataModel['horizontalAlign']) => {
                _raw.data.horizontalAlign().value.set(value);
            },
            get isSynchronizeStyle() {
                return _raw.data.isSynchronizeStyle;
            },
            /**
             * Style-synchronisation flag with the legacy-null rule applied: very
             * old widgets persisted `null`, which the FormGroup editor treated as
             * "off" — truthy check on purpose.
             */
            isSynchronized: computed<boolean>(() => Boolean(_raw.data.isSynchronizeStyle?.().value())),
            setIsSynchronizeStyle: (value: boolean) => {
                _raw.data.isSynchronizeStyle().value.set(value);
            },

            // --- Position (variant-dependent: simple grid default / extended zero) ---
            get position() {
                return _raw.data.position;
            },
            setPosition: (value: WidgetPositionLegacy | ExtendedPositionLegacy) => {
                _raw.data.position().value.set(value);
            },
        }
    );
}

export type TextForm = ReturnType<typeof createTextForm>;

/**
 * Variant alias for the zero host's `ZeroWidgetSignalForm` union (the figure
 * `XZeroForm = XForm` precedent) — both placements share one accessor shape.
 */
export type TextZeroForm = TextForm;
